import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import toast from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, CheckCircle2 } from "lucide-react";
import logo from "@/assets/logo.png";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // optional: prefill from localStorage if you want remember-me to restore email
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("remember_email");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRemember(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required");
      return;
    }
    try {
      setLoading(true);
      const res = await api.post("/users/login", {
        email: email.trim(),
        password,
      });

      // remember email if toggled
      if (remember) localStorage.setItem("remember_email", email.trim());
      else localStorage.removeItem("remember_email");

      localStorage.setItem("user", JSON.stringify(res.data.user));
      toast.success("Login successful");
      setPassword("");
      navigate("/dashboard");
    } catch {
      setPassword("");
      toast.error("Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center w-full min-h-screen px-4 py-12 bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="w-full max-w-md">
        {/* Header / Brand */}
        <div className="flex flex-col items-center mb-6">
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 mb-3">
            <div className="flex items-center justify-center w-20 h-20 bg-white shadow-md rounded-xl">
              <img
                src={logo}
                alt="Embrace Logo"
                className="object-contain w-12 h-12"
              />
            </div>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-blue-900">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-center text-gray-600">
            Sign in to manage your fleet and bookings.
          </p>
        </div>

        {/* Card */}
        <form
          onSubmit={handleLogin}
          className="relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(30,64,175,0.25)] p-6 sm:p-7"
          autoComplete="off"
        >
          {/* Email */}
          <label className="block mb-1 text-sm font-semibold text-blue-900">
            Email
          </label>
          <div className="relative mb-4">
            <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
              <Mail className="w-4 h-4 text-gray-500" />
            </span>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white/80 px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@example.com"
              required
            />
          </div>

          {/* Password */}
          <label className="block mb-1 text-sm font-semibold text-blue-900">
            Password
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 flex items-center pointer-events-none left-3">
              <Lock className="w-4 h-4 text-gray-500" />
            </span>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white/80 px-10 py-2.5 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute inset-y-0 flex items-center px-2 text-gray-600 right-2"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Remember + Links */}
          <div className="flex items-center justify-between mt-4 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span className="flex items-center gap-1 text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Remember me
              </span>
            </label>
            <Link
              to="/forgot-password"
              className="text-blue-700 hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="group relative mt-5 inline-flex w-full items-center justify-center rounded-lg bg-blue-900 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-800 disabled:opacity-70"
          >
            <span className="absolute inset-0 rounded-lg -z-10 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-xl group-hover:from-blue-500/20 group-hover:via-purple-500/20 group-hover:to-pink-500/20" />
            {loading ? "Logging in..." : "Sign in"}
          </button>

          {/* Footer links */}
          <p className="mt-4 text-sm text-center text-gray-700">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="font-medium text-blue-700 hover:underline"
            >
              Create one
            </Link>
          </p>
        </form>

        {/* Legal / Copyright */}
        <p className="mt-6 text-xs text-center text-gray-500">
          © {new Date().getFullYear()} Embrace Rentals. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
